import { EnvironmentProviders, computed, inject, makeEnvironmentProviders } from '@angular/core';
import { NAV_ITEMS, NavItem, provideNavigation } from '../../core/navigation';
import { APP_REGISTRY } from '../../apps/registry';
import { SiteAppsStore } from '../site-apps/site-apps.api';
import { SiteOverviewStore } from '../sites-data/sites.api';

/** Voci del contesto `site`, dentro un cantiere (FR-022, tavola M·01). */
export function provideSiteNavigation(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideNavigation(
      {
        id: 'site.overview',
        label: 'Panoramica',
        icon: 'home',
        route: '/sites/:siteId',
        context: 'site',
        section: 'main',
        order: 10,
        exact: true,
        mobile: true,
      },
      {
        id: 'site.activities',
        label: 'Attività',
        icon: 'activities',
        route: '/sites/:siteId/activities',
        context: 'site',
        section: 'main',
        order: 20,
        mobile: true,
      },
      {
        id: 'site.documents',
        label: 'Documenti',
        icon: 'document',
        route: '/sites/:siteId/documents',
        context: 'site',
        section: 'main',
        order: 30,
        mobile: true,
      },
      {
        id: 'site.participants',
        label: 'Partecipanti',
        icon: 'participants',
        route: '/sites/:siteId/participants',
        context: 'site',
        section: 'main',
        order: 40,
        mobile: true,
      },
      {
        id: 'site.apps',
        label: 'Catalogo App',
        icon: 'grid',
        route: '/sites/:siteId/apps',
        context: 'site',
        section: 'apps',
        order: 1000,
        exact: true,
      },
    ),
    // Una voce per ogni App del frontend, visibile solo se è attiva nel cantiere e l'utente vi ha
    // un ruolo (spec 004 FR-011, FR-016).
    ...APP_REGISTRY.map((app, index) => ({
      provide: NAV_ITEMS,
      multi: true,
      useFactory: (): NavItem => {
        const apps = inject(SiteAppsStore);
        return {
          id: `site.app.${app.code}`,
          label: app.name,
          icon: 'cast',
          appCode: app.code,
          route: `/sites/:siteId/apps/${app.code}`,
          context: 'site',
          section: 'apps',
          order: 10 + index * 10,
          visible: apps.canOpen(app.code),
        };
      },
    })),
    // "Cronologia" e "Dati del cantiere" solo per gli Amministratori del cantiere (FR-048).
    {
      provide: NAV_ITEMS,
      multi: true,
      useFactory: (): NavItem => {
        const store = inject(SiteOverviewStore);
        return {
          id: 'site.history',
          label: 'Cronologia',
          icon: 'history',
          route: '/sites/:siteId/history',
          context: 'site',
          section: 'secondary',
          order: 20,
          visible: computed(() => isAdmin(store)),
        };
      },
    },
    {
      provide: NAV_ITEMS,
      multi: true,
      useFactory: (): NavItem => {
        const store = inject(SiteOverviewStore);
        return {
          id: 'site.settings',
          label: 'Dati del cantiere',
          icon: 'document',
          route: '/sites/:siteId/settings',
          context: 'site',
          section: 'secondary',
          order: 10,
          visible: computed(() => isAdmin(store)),
        };
      },
    },
  ]);
}

function isAdmin(store: SiteOverviewStore): boolean {
  return store.overview.hasValue() && store.overview.value().site.myRole === 'admin';
}
